import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../lib/auth";
import { api, mediaUrl, getToken, BACKEND_URL } from "../lib/api";
import { Home, Search, MessageCircle, Bell, Mic, HandHelping, Settings, LogOut, Menu, X } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid, badge }) => (
    <NavLink
        to={to}
        data-testid={testid}
        className={({ isActive }) =>
            `relative flex items-center gap-2 px-3 py-2 rounded-full text-sm font-body transition-all ${
                isActive ? "text-gold" : "text-ink hover:text-gold"
            }`
        }
    >
        {({ isActive }) => (
            <>
                <Icon size={18} className={`transition-colors duration-150 ${isActive ? "text-gold" : "text-ink group-hover:text-gold"}`} />
                <span className="hidden lg:inline">{label}</span>
                {badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-bordeaux text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                        {badge}
                    </span>
                )}
            </>
        )}
    </NavLink>
);

export const Navbar = () => {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        let ws;
        const refresh = async () => {
            try {
                const { data } = await api.get("/notifications");
                setUnread(data.filter((n) => !n.read).length);
            } catch {}
        };
        refresh();
        const t = getToken();
        if (t) {
            const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws?token=${t}`;
            try {
                ws = new WebSocket(wsUrl);
                ws.onmessage = () => refresh();
            } catch {}
        }
        const iv = setInterval(refresh, 30000);
        return () => {
            clearInterval(iv);
            if (ws) ws.close();
        };
    }, [user]);

    const avatar = user?.profile?.avatar_url;

    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-bg/90 border-b border-border-line">
            <div className="container-app h-16 flex items-center justify-between">
                <Link to="/feed" data-testid="nav-logo" className="transition-opacity hover:opacity-80">
                    <Logo size={36} />
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    <NavItem to="/feed" icon={Home} label="Feed" testid="nav-feed" />
                    <NavItem to="/explore" icon={Search} label="Esplora" testid="nav-explore" />
                    <NavItem to="/messages" icon={MessageCircle} label="Messaggi" testid="nav-messages" />
                    <NavItem to="/podcast" icon={Mic} label="Podcast" testid="nav-podcast" />
                    <NavItem to="/help" icon={HandHelping} label="Aiuto" testid="nav-help" />
                    <NavItem to="/notifications" icon={Bell} label="Notifiche" testid="nav-notifications" badge={unread} />
                </nav>

                <div className="flex items-center gap-1">
                    <Link to={`/profile/${user?.id}`} data-testid="nav-my-profile" className="hidden md:flex items-center pl-2 pr-1 py-1 rounded-full hover:opacity-80 transition-opacity">
                        <div className="w-9 h-9 rounded-full overflow-hidden border border-gold bg-surface flex items-center justify-center">
                            {avatar ? (
                                <img src={mediaUrl(avatar)} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-ink text-sm font-medium">{user?.full_name?.[0]?.toUpperCase()}</span>
                            )}
                        </div>
                    </Link>
                    <Link to="/settings" data-testid="nav-settings" className="hidden md:inline-flex p-2 rounded-full icon-action" title="Impostazioni">
                        <Settings size={18} />
                    </Link>
                    <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="hidden md:inline-flex p-2 rounded-full icon-action" title="Esci">
                        <LogOut size={18} />
                    </button>
                    <button data-testid="nav-mobile-toggle" className="md:hidden p-2 rounded-full icon-action" onClick={() => setOpen(!open)}>
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t border-border-line bg-paper">
                    <div className="container-app py-3 flex flex-col gap-1">
                        <NavItem to="/feed" icon={Home} label="Feed" testid="mnav-feed" />
                        <NavItem to="/explore" icon={Search} label="Esplora" testid="mnav-explore" />
                        <NavItem to="/messages" icon={MessageCircle} label="Messaggi" testid="mnav-messages" />
                        <NavItem to="/podcast" icon={Mic} label="Podcast" testid="mnav-podcast" />
                        <NavItem to="/help" icon={HandHelping} label="Bacheca aiuti" testid="mnav-help" />
                        <NavItem to="/notifications" icon={Bell} label="Notifiche" testid="mnav-notifications" badge={unread} />
                        <NavLink to={`/profile/${user?.id}`} className="px-3 py-2 text-sm hover:text-gold" onClick={() => setOpen(false)}>Il mio profilo</NavLink>
                        <NavLink to="/settings" className="px-3 py-2 text-sm hover:text-gold" onClick={() => setOpen(false)}>Impostazioni</NavLink>
                        <button onClick={() => { logout(); nav("/"); }} className="text-left px-3 py-2 text-sm text-bordeaux">Esci</button>
                    </div>
                </div>
            )}
        </header>
    );
};
